import React, { useMemo } from 'react';
import type { VoucherFormData } from './VoucherForm';
import { Eye } from 'lucide-react';
import { convertDateTimeLocalToISO } from '@/lib/utils';
import { VoucherStatus } from '@/api/model/voucherStatus';
import { useListProviders } from '@/api/voucher-providers/voucher-providers';
import type { VoucherProviderResponse } from '@/api/model/voucherProviderResponse';
import type { VoucherResponse } from '@/api/model/voucherResponse';
import { VoucherCard } from './VoucherCard';

interface VoucherPreviewProps {
  formData: VoucherFormData;
}

export const VoucherPreview: React.FC<VoucherPreviewProps> = ({ formData }) => {
  const { data: providersData } = useListProviders();

  const nowIso = useMemo(() => new Date().toISOString(), []);

  const selectedProvider = useMemo(
    () => providersData?.items?.find(provider => provider.id === formData.provider_id),
    [providersData?.items, formData.provider_id]
  );

  const previewProvider: VoucherProviderResponse = useMemo(() => {
    if (selectedProvider) {
      return selectedProvider;
    }

    return {
      id: 'preview-provider',
      name: formData.title || 'Vorschau Anbieter',
      email: 'preview@example.com',
      address: 'Adresse wird ergänzt',
      phone: null,
      short_description: null,
      logo_url: null,
      category: 'GASTRONOMIE' as VoucherProviderResponse['category'],
      is_active: true,
      created_at: nowIso,
      updated_at: nowIso,
    };
  }, [selectedProvider, formData.title, nowIso]);

  const previewVoucher: VoucherResponse = useMemo(() => {
    const sanitizedImages = Array.isArray(formData.images)
      ? formData.images
          .map(image => {
            if (typeof image === 'string') {
              return image.trim();
            }
            if (image == null) {
              return '';
            }
            return String(image).trim();
          })
          .filter(url => url.length > 0)
          .slice(0, 5)
      : [];

    const validFrom = formData.valid_from ? convertDateTimeLocalToISO(formData.valid_from) : nowIso;
    const validUntil = formData.valid_until ? convertDateTimeLocalToISO(formData.valid_until) : nowIso;

    return {
      id: `preview-${formData.provider_id ?? 'voucher'}-${sanitizedImages.join(',')}`,
      title: formData.title || previewProvider.name,
      provider_id: formData.provider_id || previewProvider.id,
      description: formData.description || '',
      conditions: formData.conditions || '',
      value: typeof formData.value === 'number' ? formData.value : Number(formData.value) || 0,
      valid_from: validFrom,
      valid_until: validUntil,
      max_redemptions: formData.max_redemptions ?? null,
      images: sanitizedImages,
      redemption_type: formData.redemption_type,
      status: formData.status,
      is_active: formData.status !== VoucherStatus.ARCHIVED,
      current_redemptions: 0,
      created_by: 'preview-user',
      created_at: nowIso,
      updated_at: nowIso,
      provider: previewProvider,
    };
  }, [
    formData.conditions,
    formData.description,
    formData.images,
    formData.max_redemptions,
    formData.provider_id,
    formData.redemption_type,
    formData.status,
    formData.title,
    formData.valid_from,
    formData.valid_until,
    formData.value,
    nowIso,
    previewProvider,
  ]);

  return (
    <div className='border border-neutral-hellgrau rounded-lg p-6'>
      <div className='flex items-center gap-2 mb-6'>
        <Eye className='h-6 w-6 text-swd-tuerkis-darker' />
        <span className='text-neutral-dunkelgrau text-xl font-bold leading-7'>Vorschau</span>
      </div>

      <VoucherCard voucher={previewVoucher} isPreview={true} onClick={undefined} />
    </div>
  );
};
